import { supabase } from './supabase'
import type { College, Review, BlogPost, PersonalizedList, NewsletterSubscriber, User, Inquiry, PersonalizedListSubmission, BlogFAQ, CollegeFAQ } from './supabase'

// Helper function to generate slug from name
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// College operations with real Supabase connection
export const collegeService = {
  async getAll(filters?: {
    category?: string
    state?: string
    stream?: string
    search?: string
  }) {
    let query = supabase
      .from('colleges')
      .select('*')
      .order('rating', { ascending: false })

    if (filters?.category && filters.category !== 'all') {
      query = query.eq('category', filters.category)
    }

    if (filters?.state && filters.state !== 'all') {
      query = query.eq('state', filters.state)
    }

    if (filters?.stream && filters.stream !== 'all') {
      query = query.eq('stream', filters.stream)
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,location.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data as College[]
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('colleges')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as College
  },

  async getBySlug(slug: string) {
    const { data, error } = await supabase
      .from('colleges')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error) throw error
    return data as College
  },

  async getByState(state: string, stream?: string) {
    let query = supabase
      .from('colleges')
      .select('*')
      .eq('state', state)
      .order('rating', { ascending: false })

    if (stream && stream !== 'all') {
      query = query.eq('stream', stream)
    }

    const { data, error } = await query

    if (error) throw error
    return data as College[]
  },

  async getByStream(stream: string, state?: string) {
    let query = supabase
      .from('colleges')
      .select('*')
      .eq('stream', stream)
      .order('rating', { ascending: false })

    if (state && state !== 'all') {
      query = query.eq('state', state)
    }

    const { data, error } = await query

    if (error) throw error
    return data as College[]
  },

  async getFeatured(limit = 6) {
    const { data, error } = await supabase
      .from('colleges')
      .select('*')
      .gte('rating', 4.5)
      .order('rating', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data as College[]
  },

  async create(college: Omit<College, 'id' | 'created_at' | 'updated_at'>) {
    // If no slug provided, generate one from name
    let slug = college.slug;
    if (!slug && college.name) {
      slug = generateSlug(college.name) + '-' + Date.now().toString(36);
    }
    
    const { data, error } = await supabase
      .from('colleges')
      .insert([{
        ...college,
        slug,
        // Set default SEO values if not provided
        meta_title: college.meta_title || college.name,
        meta_description: college.meta_description || college.description,
        canonical_url: college.canonical_url || `/college/${slug}`,
        og_title: college.og_title || college.name,
        og_description: college.og_description || college.description,
        og_image: college.og_image || college.image_url,
        twitter_title: college.twitter_title || college.name,
        twitter_description: college.twitter_description || college.description,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as College
  },

  async update(id: string, updates: Partial<College>) {
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    // If slug is not provided but name is being updated, generate new slug
    if (updates.name && !updates.slug) {
      updateData.slug = generateSlug(updates.name) + '-' + Date.now().toString(36);
    }

    const { data, error } = await supabase
      .from('colleges')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as College
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('colleges')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  }
}

// Review operations with real Supabase connection
export const reviewService = {
  async getByCollegeId(collegeId: string) {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('college_id', collegeId)
      .eq('approved', true)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data as Review[]
  },

  async getAll(filters?: {
    approved?: boolean
    college_id?: string
  }) {
    let query = supabase
      .from('reviews')
      .select(`
        *,
        colleges (
          name,
          image_url
        )
      `)
      .order('created_at', { ascending: false })

    if (filters?.approved !== undefined) {
      query = query.eq('approved', filters.approved)
    }

    if (filters?.college_id) {
      query = query.eq('college_id', filters.college_id)
    }

    const { data, error } = await query

    if (error) throw error
    return data as (Review & { colleges: { name: string; image_url: string } })[]
  },

  async create(review: Omit<Review, 'id' | 'created_at' | 'updated_at' | 'helpful_count'>) {
    const { data, error } = await supabase
      .from('reviews')
      .insert([{
        ...review,
        helpful_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as Review
  },

  async approve(id: string) {
    const { data, error } = await supabase
      .from('reviews')
      .update({ 
        approved: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Review
  },

  async updateHelpfulCount(reviewId: string, increment = true) {
    const { data: review, error: fetchError } = await supabase
      .from('reviews')
      .select('helpful_count')
      .eq('id', reviewId)
      .single()

    if (fetchError) throw fetchError

    const newCount = review.helpful_count + (increment ? 1 : -1)

    const { data, error } = await supabase
      .from('reviews')
      .update({ 
        helpful_count: Math.max(0, newCount),
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId)
      .select()
      .single()

    if (error) throw error
    return data as Review
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  }
}

// Blog operations with real Supabase connection
export const blogService = {
  async getAll(filters?: {
    category?: string
    search?: string
    featured?: boolean
    published?: boolean
  }) {
    let query = supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false })

    // Only show published posts on frontend, all posts in admin
    if (filters?.published !== undefined) {
      query = query.eq('published', filters.published)
    }

    if (filters?.category && filters.category !== 'all') {
      query = query.eq('category', filters.category)
    }

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,excerpt.ilike.%${filters.search}%`)
    }

    if (filters?.featured) {
      query = query.eq('featured', true)
    }

    const { data, error } = await query

    if (error) throw error
    return data as BlogPost[]
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as BlogPost
  },

  async getBySlug(slug: string) {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .single()

    if (error) throw error
    return data as BlogPost
  },

  async create(post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>) {
    // Generate slug if not provided
    let slug = post.slug;
    if (!slug && post.title) {
      slug = post.title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '') + '-' + Date.now().toString(36);
    }

    const { data, error } = await supabase
      .from('blog_posts')
      .insert([{
        ...post,
        slug,
        // Set default SEO values if not provided
        meta_title: post.meta_title || post.title,
        meta_description: post.meta_description || post.excerpt,
        canonical_url: post.canonical_url || `/blog/${slug}`,
        og_title: post.og_title || post.title,
        og_description: post.og_description || post.excerpt,
        og_image: post.og_image || post.image_url,
        twitter_title: post.twitter_title || post.title,
        twitter_description: post.twitter_description || post.excerpt,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as BlogPost
  },

  async update(id: string, updates: Partial<BlogPost>) {
    const { data, error } = await supabase
      .from('blog_posts')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as BlogPost
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  },

  async getFeatured(limit = 3) {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('published', true)
      .eq('featured', true)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data as BlogPost[]
  },

  // Get page SEO settings
  async getPageSEO(pageKey: string) {
    const { data, error } = await supabase
      .from('page_seo_settings')
      .select('*')
      .eq('page_key', pageKey)
      .maybeSingle()

    if (error) {
      console.error('Error fetching page SEO:', error)
      return null
    }
    return data
  },

  // Update page SEO settings
  async updatePageSEO(pageKey: string, seoData: any) {
    console.log('Saving SEO settings to database:', { pageKey, seoData })

    const { data, error } = await supabase
      .from('page_seo_settings')
      .upsert({
        page_key: pageKey,
        meta_title: seoData.meta_title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        focus_keyword: seoData.focus_keyword,
        canonical_url: seoData.canonical_url,
        og_title: seoData.og_title,
        og_description: seoData.og_description,
        og_image: seoData.og_image,
        twitter_title: seoData.twitter_title,
        twitter_description: seoData.twitter_description,
        schema_type: seoData.schema_type || 'WebPage',
        updated_at: new Date().toISOString()
      })
      .select()
      .single()

    if (error) {
      console.error('Error saving page SEO:', error)
      throw error
    }
    
    console.log('SEO settings saved successfully:', data)
    return data
  }
}

// Newsletter operations with better error handling
export const newsletterService = {
  async subscribe(email: string) {
    try {
      // First check if email already exists
      const { data: existing, error: checkError } = await supabase
        .from('newsletter_subscribers')
        .select('id, active')
        .eq('email', email.toLowerCase().trim())
        .single()

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 is "no rows returned" which is expected for new emails
        throw checkError
      }

      if (existing) {
        if (existing.active) {
          throw new Error('Email already subscribed')
        } else {
          // Reactivate subscription
          const { data, error } = await supabase
            .from('newsletter_subscribers')
            .update({ 
              active: true,
              subscribed_at: new Date().toISOString()
            })
            .eq('id', existing.id)
            .select()
            .single()

          if (error) throw error
          return data as NewsletterSubscriber
        }
      }

      // Create new subscription
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .insert([{ 
          email: email.toLowerCase().trim(),
          active: true,
          subscribed_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('Email already subscribed')
        }
        throw error
      }
      
      return data as NewsletterSubscriber
    } catch (error) {
      console.error('Newsletter subscription error:', error)
      throw error
    }
  },

  async unsubscribe(email: string) {
    const { error } = await supabase
      .from('newsletter_subscribers')
      .update({ active: false })
      .eq('email', email.toLowerCase().trim())

    if (error) throw error
  },

  async getAll() {
    const { data, error } = await supabase
      .from('newsletter_subscribers')
      .select('*')
      .eq('active', true)
      .order('subscribed_at', { ascending: false })

    if (error) throw error
    return data as NewsletterSubscriber[]
  }
}

// Personalized list operations
export const personalizedListService = {
  async create(listData: Omit<PersonalizedList, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('personalized_lists')
      .insert([{
        ...listData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as PersonalizedList
  },

  async getByUserId(userId: string) {
    const { data, error } = await supabase
      .from('personalized_lists')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data as PersonalizedList[]
  },

  async getAll(filters?: {
    status?: string
  }) {
    let query = supabase
      .from('personalized_lists')
      .select(`
        *,
        users (
          full_name,
          email
        )
      `)
      .order('created_at', { ascending: false })

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status)
    }

    const { data, error } = await query

    if (error) throw error
    return data as (PersonalizedList & { users: { full_name: string; email: string } })[]
  },

  async updateStatus(id: string, status: 'pending' | 'processed' | 'sent') {
    const { data, error } = await supabase
      .from('personalized_lists')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as PersonalizedList
  }
}

// Personalized List Submissions (New)
export const personalizedListSubmissionService = {
  async create(submission: Omit<PersonalizedListSubmission, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('personalized_list_submissions')
      .insert([{
        ...submission,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as PersonalizedListSubmission
  },

  async getAll(filters?: {
    payment_status?: string
    search?: string
  }) {
    let query = supabase
      .from('personalized_list_submissions')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters?.payment_status && filters.payment_status !== 'all') {
      query = query.eq('payment_status', filters.payment_status)
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data as PersonalizedListSubmission[]
  },

  async updatePaymentStatus(id: string, status: 'pending' | 'completed' | 'failed', paymentId?: string) {
    const updateData: any = {
      payment_status: status,
      updated_at: new Date().toISOString()
    };

    if (paymentId) {
      updateData.payment_id = paymentId;
    }

    const { data, error } = await supabase
      .from('personalized_list_submissions')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as PersonalizedListSubmission
  }
}

// User operations
export const userService = {
  async getAll(filters?: {
    role?: string
    search?: string
  }) {
    let query = supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters?.role && filters.role !== 'all') {
      query = query.eq('role', filters.role)
    }

    if (filters?.search) {
      query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data as User[]
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as User
  },

  async updateRole(id: string, role: 'student' | 'admin' | 'counselor') {
    const { data, error } = await supabase
      .from('users')
      .update({ 
        role,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as User
  }
}

// Contact/Inquiry operations with better error handling
export const inquiryService = {
  async create(inquiry: Omit<Inquiry, 'id' | 'created_at' | 'updated_at' | 'status'>) {
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .insert([{
          name: inquiry.name.trim(),
          email: inquiry.email.toLowerCase().trim(),
          subject: inquiry.subject.trim(),
          message: inquiry.message.trim(),
          status: 'new',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (error) {
        console.error('Supabase inquiry creation error:', error)
        throw error
      }
      
      return data as Inquiry
    } catch (error) {
      console.error('Inquiry creation error:', error)
      throw error
    }
  },

  async getAll(filters?: {
    status?: string
    search?: string
  }) {
    let query = supabase
      .from('inquiries')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status)
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data as Inquiry[]
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('inquiries')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as Inquiry
  },

  async updateStatus(id: string, status: 'new' | 'read' | 'replied' | 'closed') {
    const { data, error } = await supabase
      .from('inquiries')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Inquiry
  },

  async markAsRead(id: string) {
    return this.updateStatus(id, 'read')
  },

  async markAsReplied(id: string) {
    return this.updateStatus(id, 'replied')
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('inquiries')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  }
}

// Search operations
export const searchService = {
  async searchColleges(query: string, filters?: {
    category?: string
    state?: string
  }) {
    return collegeService.getAll({
      search: query,
      ...filters
    })
  }
}

// Blog FAQ operations
export const blogFAQService = {
  async getByBlogId(blogId: string) {
    const { data, error } = await supabase
      .from('blog_faqs')
      .select('*')
      .eq('blog_id', blogId)
      .eq('is_active', true)
      .order('order_index', { ascending: true })

    if (error) throw error
    return data as BlogFAQ[]
  },

  async create(faq: Omit<BlogFAQ, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('blog_faqs')
      .insert([{
        ...faq,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as BlogFAQ
  },

  async update(id: string, updates: Partial<BlogFAQ>) {
    const { data, error } = await supabase
      .from('blog_faqs')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as BlogFAQ
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('blog_faqs')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  }
}

// College FAQ operations
export const collegeFAQService = {
  async getByCollegeId(collegeId: string) {
    const { data, error } = await supabase
      .from('college_faqs')
      .select('*')
      .eq('college_id', collegeId)
      .eq('is_active', true)
      .order('order_index', { ascending: true })

    if (error) throw error
    return data as CollegeFAQ[]
  },

  async create(faq: Omit<CollegeFAQ, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('college_faqs')
      .insert([{
        ...faq,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data as CollegeFAQ
  },

  async update(id: string, updates: Partial<CollegeFAQ>) {
    const { data, error } = await supabase
      .from('college_faqs')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as CollegeFAQ
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('college_faqs')
      .delete()
      .eq('id', id)

    if (error) throw error
    return true
  }
}